import { Decimal } from 'decimal.js-light'
import { isNil } from 'lodash'

import { OrderSide } from '@/types/enums/order'

/**
 * Calculates the number of bonds based on quantity and par value using Decimal.js for precision.
 * The number of bonds is calculated as: quantity / par_value
 *
 * @param {Object} params - The calculation parameters
 * @param {Decimal} params.quantity - The total quantity of the order as a Decimal
 * @param {Decimal} params.parValue - The par value of a single bond as a Decimal
 * @returns {Decimal} The calculated number of bonds as a Decimal
 *
 * @example
 * // Calculate number of bonds for quantity 10000 with par value 1000
 * getNumBonds({ quantity: new Decimal(10000), parValue: new Decimal(1000) })
 * // Returns: Decimal(10)
 */
export function getNumBonds({
  quantity,
  parValue,
}: {
  quantity: Decimal
  parValue: Decimal
}): Decimal {
  return quantity.div(parValue)
}

/**
 * Calculates the number of bonds based on quantity and par value using Decimal.js for precision.
 * The number of bonds is calculated as: quantity / par_value
 *
 * @deprecated Prefer getNumBonds for pure Decimal operations to avoid precision loss from number conversions.
 *
 * @param {Object} params - The calculation parameters
 * @param {number} params.quantity - The total quantity of the order
 * @param {number} params.parValue - The par value of a single bond
 * @returns {Decimal} The calculated number of bonds as a Decimal
 *
 * @example
 * // Calculate number of bonds for quantity 10000 with par value 1000
 * getNumBondsDecimal({ quantity: 10000, parValue: 1000 })
 * // Returns: Decimal(10)
 */
export function getNumBondsDecimal({
  quantity,
  parValue,
}: {
  quantity: number
  parValue: number
}): Decimal {
  return getNumBonds({
    quantity: new Decimal(quantity),
    parValue: new Decimal(parValue),
  })
}

/**
 * Calculates the principal amount for a bond order based on quantity and price using Decimal.js for precision.
 * The principal amount is calculated as: (price / 100) * quantity * principalFactor.
 *
 * @param {Object} params - The calculation parameters
 * @param {Decimal} params.quantity - The number of bonds in the order as a Decimal
 * @param {Decimal} params.price - The price of the bond as a percentage of par (e.g., 99.5 for 99.5%)
 * @param {Decimal} params.principalFactor - A decimal multiplier that adjusts the principal amount, typically between 0 and 1 but could be greater than 1. Defaults to 1 if not provided.
 * @returns {Decimal} The calculated principal amount as a Decimal rounded to 4 decimal places
 *
 * @example
 * // Calculate principal for 10 bonds at 99.5% with a 0.75 principal factor
 * getPrincipal({ quantity: new Decimal(10), price: new Decimal(99.5), principalFactor: new Decimal(0.75) })
 * // Returns: Decimal(746.25)
 */
export function getPrincipal({
  quantity,
  price,
  principalFactor = new Decimal(1),
}: {
  quantity: Decimal
  price: Decimal
  principalFactor?: Decimal
}): Decimal {
  return price.div(100).mul(quantity).mul(principalFactor).toDecimalPlaces(4)
}

/**
 * Calculates the principal amount for a bond order based on quantity and price using Decimal.js for precision.
 * The principal amount is calculated as: (price / 100) * quantity * principalFactor.
 *
 * @deprecated Prefer getPrincipal for pure Decimal operations to avoid precision loss from number conversions.
 *
 * @param {Object} params - The calculation parameters
 * @param {number} params.quantity - The number of bonds in the order
 * @param {number} params.price - The price of the bond as a percentage of par (e.g., 99.5 for 99.5%)
 * @param {number} params.principalFactor - A decimal multiplier that adjusts the principal amount, typically between 0 and 1 but could be greater than 1. Defaults to 1 if not provided.
 * @returns {Decimal} The calculated principal amount as a Decimal rounded to 4 decimal places
 *
 * @example
 * // Calculate principal for 10 bonds at 99.5% with a 0.75 principal factor
 * getPrincipalDecimal({ quantity: 10, price: 99.5, principalFactor: 0.75 })
 * // Returns: Decimal(746.25)
 */
export function getPrincipalDecimal({
  quantity,
  price,
  principalFactor = 1,
}: {
  quantity: number
  price: number
  principalFactor?: number
}): Decimal {
  return getPrincipal({
    quantity: new Decimal(quantity),
    price: new Decimal(price ?? 0),
    principalFactor: new Decimal(!isNil(principalFactor) ? principalFactor : 1),
  })
}

/**
 * Calculates the accrued interest for a bond based on accrued and numBonds using Decimal.js for precision.
 * The accrued interest is calculated as: accrued * numBonds
 *
 * @param {Object} params - The calculation parameters
 * @param {Decimal} params.accrued - The accrued interest per bond as a Decimal
 * @param {Decimal} params.numBonds - The number of bonds as a Decimal
 * @returns {Decimal} The calculated accrued interest rounded to 4 decimal places
 *
 * @example
 * // Calculate accrued interest for 10 bonds with 1.25 accrued per bond
 * getAccruedInterest({ accrued: new Decimal(1.25), numBonds: new Decimal(10) })
 * // Returns: Decimal(12.5)
 */
export function getAccruedInterest({
  accrued,
  numBonds,
}: {
  accrued: Decimal
  numBonds: Decimal
}): Decimal {
  return accrued.mul(numBonds).toDecimalPlaces(4)
}

/**
 * Calculates the accrued interest for a bond based on accrued and numBonds using Decimal.js for precision.
 * The accrued interest is calculated as: accrued * numBonds
 *
 * @deprecated Prefer getAccruedInterest for pure Decimal operations to avoid precision loss from number conversions.
 *
 * @param {Object} params - The calculation parameters
 * @param {number} params.accrued - The accrued interest per bond
 * @param {number} params.numBonds - The number of bonds
 * @returns {Decimal} The calculated accrued interest rounded to 4 decimal places
 *
 * @example
 * // Calculate accrued interest for 10 bonds with 1.25 accrued per bond
 * getAccruedInterestDecimal({ accrued: 1.25, numBonds: 10 })
 * // Returns: Decimal(12.5)
 */
export function getAccruedInterestDecimal({
  accrued,
  numBonds,
}: {
  accrued: number
  numBonds: number
}): Decimal {
  return getAccruedInterest({
    accrued: new Decimal(accrued),
    numBonds: new Decimal(numBonds),
  })
}

/**
 * Calculates the market value of a bond order.
 * Matches propman (instrument/marketvalue.go) and suboptimal (transformer/processors.py):
 *   mv = qty * (price / 100) * principalFactor + (qty / parValue) * accruedInterest
 * Note: principalFactor is not applied to accruedInterest in propman or suboptimal
 */
export function calculateMarketValue({
  quantity,
  limitPrice,
  parValue,
  principalFactor,
  accruedInterest,
}: {
  quantity: number
  limitPrice: number
  parValue: number
  principalFactor?: number | null
  accruedInterest?: number | null
}): number {
  if (parValue === 0) return 0

  const qty = new Decimal(quantity)
  const price = new Decimal(limitPrice)
  const pv = new Decimal(parValue)
  const pf = new Decimal(principalFactor ?? 1)
  const ai = new Decimal(accruedInterest ?? 0)
  return qty.mul(price.div(100)).mul(pf).add(qty.div(pv).mul(ai)).toNumber()
}

/**
 * Clean market value of a bond position: qty * (price / 100) * principalFactor.
 * Accrued interest is excluded — use this for valuation displays (per-unit MV,
 * weight % columns). Use {@link calculateMarketValue} for order/cash-impact math.
 */
export function calculateCleanMarketValue({
  quantity,
  limitPrice,
  principalFactor,
}: {
  quantity: number
  limitPrice: number
  principalFactor?: number | null
}): number {
  const qty = new Decimal(quantity)
  const price = new Decimal(limitPrice)
  const pf = new Decimal(principalFactor ?? 1)
  return qty.mul(price.div(100)).mul(pf).toNumber()
}

/**
 * Adjusts a given bond price by rounding it based on the order side and specified precision.
 * The goal is to provide  a slight "cushion" in showing pricing, increasing the odds of execution.
 *
 * Buyers like lower prices, so when we set a limit price if we add a buffer *upwards* to the price,
 * it makes it less likely quotes breach the limit price and fail to execute.
 * For example, if the quote price is 103.78543, setting the limit price to 103.7855 increases the likelihood
 * the quote will execute.
 *
 * Sellers like higher prices, so when we set a limit price if we add a buffer *downwards* to the price
 * for the same reason.
 *
 * The default precision is 4 decimal places.
 *
 * @param price - The price to adjust. Can be a number, null, or undefined.
 * @param side - The order side (Buy/Sell) which determines the direction of rounding.
 * @param {object} [options] - Optional parameters for the adjustment.
 * @param {number} [options.precision=4] - The number of decimal places to round to. Defaults to 4.
 * @returns The adjusted price as a number, rounded according to the side and precision,
 *          or the original price if it was null or undefined.
 *
 * @example
 * // Buy order: rounds 103.78543 up to 4 decimal places
 * adjustPriceBySideAndPrecision(103.78543, OrderSide.Buy, { precision: 4 })
 * // Returns: 103.7855
 *
 * @example
 * // Sell order: rounds 103.78547 down to 4 decimal places
 * adjustPriceBySideAndPrecision(103.78547, OrderSide.Sell, { precision: 4 })
 * // Returns: 103.7854
 *
 * @example
 * // Price 103.7854 with default precision 4 for a Buy order (no change needed if already at a ceilable point)
 * adjustPriceBySideAndPrecision(103.7854, OrderSide.Buy)
 * // Returns: 103.7854
 *
 * @example
 * // Null price input
 * adjustPriceBySideAndPrecision(null, OrderSide.Buy)
 * // Returns: null
 */
export function adjustPriceBySideAndPrecision(
  price: number | null | undefined,
  side: OrderSide,
  options?: { precision?: number },
): number | null | undefined {
  if (isNil(price)) {
    return price
  }
  const precision = options?.precision ?? 4
  const decimalPrice = new Decimal(price)
  if (side === OrderSide.Buy) {
    return decimalPrice
      .toDecimalPlaces(precision, Decimal.ROUND_CEIL)
      .toNumber()
  }
  return decimalPrice.toDecimalPlaces(precision, Decimal.ROUND_FLOOR).toNumber()
}
